import { GridItem, LayoutGrid, TypographyV3 } from '@jouzen/ecom-components';
import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';

import Motion from '@/app/components/Motion';
import SectionHeading from '@/app/components/SectionHeading';
import useOuraGen4Enabled from '@/app/hooks/useOuraGen4Enabled';

import { PopHeroChiclet } from './PopHeroChiclet';
import { PopHeroEyebrow } from './PopHeroEyebrow';
import { PopHeroShopCta } from './PopHeroShopCta';

export function PopHeroContent(): JSX.Element {
  const t = useTranslations();
  const gen4Enabled = useOuraGen4Enabled();

  return (
    <LayoutGrid
      className={cx(
        'relative z-50 mt-auto size-full content-between pt-[4vh] lg:pt-[25vh]',
        {
          'maxlg:grid-rows-[10fr_auto_auto]': !gen4Enabled,
          'grid-rows-[auto_auto_10fr] md:grid-rows-[auto_10fr]': gen4Enabled,
        },
      )}
      data-cy="pop_hero_content"
      rowGap={0}
    >
      <GridItem
        className="maxlg:flex maxlg:flex-col maxlg:items-center"
        colEnd={{ sm: 'main' }}
        colStart={{ sm: 'main' }}
      >
        {gen4Enabled && <PopHeroEyebrow />}
        <SectionHeading
          animated
          content="a_really_smart_ring_title"
          emAnimationProps={{
            transition: { duration: 1.5, type: 'tween', delay: 0.5 },
          }}
          indentation="lg:pl-24"
          multiline
          typographyProps={{
            className:
              'leading-tighter tracking-tighter maxlg:text-center lg:leading-none',
            color: 'inherit',
            Element: 'h1',
            variant: 'h1',
          }}
          wrapperAnimationProps={{
            transition: { duration: 1, type: 'tween', delay: 0.5 },
          }}
        />
      </GridItem>
      <GridItem
        className={cx({
          'flex items-end maxlg:order-2 maxlg:pt-7': gen4Enabled,
        })}
        colEnd={{
          sm: 'main',
          md: 11,
          lg: 9,
          xl: 8,
        }}
        colStart={{ sm: 'main' }}
        data-cy="pop_hero_copy_wrapper"
      >
        <Motion
          className="max-w-96 maxmd:mx-auto"
          initial={{ opacity: 0 }}
          transition={{ duration: 2, type: 'tween', delay: 1.25 }}
          viewport={{ once: true }}
          whileInView={{ opacity: 1 }}
        >
          <TypographyV3
            className="relative z-10 text-center text-base md:text-left md:text-body-lg"
            color="inherit"
          >
            {t('pop_hero_copy')}
          </TypographyV3>
        </Motion>
      </GridItem>
      <GridItem
        className={cx('flex justify-center', {
          'md:justify-end xl:hidden': !gen4Enabled,
          'lg:hidden maxlg:order-1 maxlg:pt-7': gen4Enabled,
        })}
        colEnd={{ sm: 'main' }}
        colStart={{
          sm: 'main',
          md: gen4Enabled ? 'main' : 12,
          lg: gen4Enabled ? 'main' : 10,
          xl: gen4Enabled ? 'main' : 9,
        }}
        data-cy="pop_hero_cta_wrapper"
      >
        <Motion
          initial={{ opacity: 0 }}
          transition={{ duration: 2, type: 'tween', delay: 1.25 }}
          viewport={{ once: true }}
          whileInView={{ opacity: 1 }}
        >
          <PopHeroShopCta />
        </Motion>
      </GridItem>
      {gen4Enabled && (
        <GridItem
          className="flex items-end justify-end maxlg:order-3 maxlg:pt-7"
          colEnd={{ sm: 'main' }}
          colStart={{ sm: 'main', md: 11, lg: 9 }}
        >
          <PopHeroChiclet />
        </GridItem>
      )}
    </LayoutGrid>
  );
}
